interface GameBoardProps {
  guesses: string[];
  currentGuess: string;
  targetWord: string;
  gameStatus: 'playing' | 'won' | 'lost';
  darkMode: boolean;
}

export default function GameBoard({ guesses, currentGuess, targetWord, gameStatus, darkMode }: GameBoardProps) {
  const maxGuesses = 6;
  const wordLength = 5;

  const getLetterColor = (letter: string, index: number, word: string) => {
    if (targetWord[index] === letter) {
      return 'bg-teal-500 border-teal-500 text-white';
    } else if (targetWord.includes(letter)) {
      return 'bg-amber-500 border-amber-500 text-white';
    } else {
      return darkMode ? 'bg-gray-700 border-gray-700 text-white' : 'bg-gray-400 border-gray-400 text-white';
    }
  };

  const renderRow = (rowIndex: number) => {
    const isCurrentRow = rowIndex === guesses.length;
    const isCompletedRow = rowIndex < guesses.length;
    const word = isCompletedRow ? guesses[rowIndex] : isCurrentRow ? currentGuess : '';

    return (
      <div key={rowIndex} className="flex gap-2 justify-center">
        {Array.from({ length: wordLength }).map((_, colIndex) => {
          const letter = word[colIndex] || '';
          const hasLetter = letter !== '';
          
          let cellClass = `w-14 h-14 border-2 flex items-center justify-center text-2xl font-bold rounded-md transition-all duration-300 ${
            darkMode ? 'border-gray-600' : 'border-gray-300'
          }`;

          if (isCompletedRow) {
            cellClass = `w-14 h-14 border-2 flex items-center justify-center text-2xl font-bold rounded-md transition-all duration-300 ${getLetterColor(letter, colIndex, word)} animate-flip`;
          } else if (hasLetter) {
            cellClass = `w-14 h-14 border-2 flex items-center justify-center text-2xl font-bold rounded-md transition-all duration-300 ${
              darkMode ? 'border-gray-500 text-white bg-gray-800' : 'border-gray-500 text-gray-900 bg-white'
            } scale-110`;
          } else {
            cellClass = `w-14 h-14 border-2 flex items-center justify-center text-2xl font-bold rounded-md transition-all duration-300 ${
              darkMode ? 'border-gray-600 text-white bg-gray-800' : 'border-gray-300 text-gray-900 bg-white'
            }`;
          }

          return (
            <div
              key={colIndex}
              className={cellClass}
              style={{ animationDelay: isCompletedRow ? `${colIndex * 100}ms` : '0ms' }}
            >
              {letter}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: maxGuesses }).map((_, index) => renderRow(index))}
      
      {gameStatus === 'won' && (
        <div className={`mt-4 text-center text-xl font-semibold ${darkMode ? 'text-teal-400' : 'text-teal-600'} animate-bounce`}>
          Congratulations! You won! 🎉
        </div>
      )}
      
      {gameStatus === 'lost' && (
        <div className={`mt-4 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <p className="text-xl font-semibold">Game Over!</p>
          <p className="text-lg mt-2">The word was: <span className="font-bold text-teal-500">{targetWord}</span></p>
        </div>
      )}
    </div>
  );
}
